import { useState } from "react";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { dayjs } from "@thalamos/common";
import type { ExternalPatientLinkEnhanced } from "@thalamos/aspire-common";

import { getBadgeColor } from "./helpers.js";
import { ukLocalFormatDateTime } from "~/util.js";

export type ManualPullButtonProps = Pick<
  ExternalPatientLinkEnhanced,
  "externalSystemType" | "externalSystemDisplayName" | "dateLastProcessed"
> & {
  onSync: () => Promise<void>;
  allExternalSystemDisplayNames: string[];
  displayMode: "button" | "menu-item";
  disabled?: boolean;
};

export const ManualPullButton = ({
  externalSystemType,
  externalSystemDisplayName,
  dateLastProcessed,
  onSync,
  allExternalSystemDisplayNames,
  displayMode,
  disabled,
}: ManualPullButtonProps) => {
  const { t } = useTranslation();
  const [synchronizing, setSynchronizing] = useState(false);

  const buttonTitle =
    externalSystemType === "rio"
      ? (t(
          "components.externalPatientLink.manualPullButton.syncWithRio",
        ) as string)
      : externalSystemType === "pds"
        ? (t(
            "components.externalPatientLink.manualPullButton.syncWithPds",
          ) as string)
        : (t("components.externalPatientLink.manualPullButton.sync") as string);

  const badgeColor = getBadgeColor(dateLastProcessed);

  return (
    <Box data-testid="manual-pull-button">
      <Tooltip
        placement="left"
        enterDelay={500}
        arrow
        title={
          <ToolTipContent
            externalSystemDisplayName={externalSystemDisplayName}
            dateLastProcessed={dateLastProcessed}
            allExternalSystemDisplayNames={allExternalSystemDisplayNames}
          />
        }
      >
        <span>
          {displayMode === "button" ? (
            <Button
              variant="outlined"
              startIcon={
                <StartIcon
                  synchronizing={synchronizing}
                  badgeColor={badgeColor}
                />
              }
              sx={{ minWidth: "max-content" }}
              disabled={synchronizing || disabled}
              onClick={async () => {
                setSynchronizing(true);
                try {
                  await onSync();
                } finally {
                  setSynchronizing(false);
                }
              }}
            >
              {buttonTitle}
            </Button>
          ) : (
            <>
              <MenuItem
                disabled={synchronizing}
                onClick={async () => {
                  setSynchronizing(true);
                  try {
                    await onSync();
                  } finally {
                    setSynchronizing(false);
                  }
                }}
                sx={{
                  justifyContent: "flex-start",
                  py: 1.5,
                  minWidth: "240px",
                  gap: 1,
                  textTransform: "none",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "1.5rem",
                    color: "text.secondary",
                  }}
                >
                  <StartIcon
                    synchronizing={synchronizing}
                    badgeColor={badgeColor}
                  />
                </Box>
                <Typography noWrap>{buttonTitle}</Typography>
              </MenuItem>
            </>
          )}
        </span>
      </Tooltip>
    </Box>
  );
};

export const ToolTipContent = ({
  externalSystemDisplayName,
  dateLastProcessed,
  allExternalSystemDisplayNames,
}: Pick<
  ManualPullButtonProps,
  | "externalSystemDisplayName"
  | "dateLastProcessed"
  | "allExternalSystemDisplayNames"
>) => {
  return (
    <Stack>
      <Typography>{externalSystemDisplayName}</Typography>
      {dateLastProcessed && (
        <Typography>
          Last processed{" "}
          {ukLocalFormatDateTime(dayjs(dateLastProcessed).toISOString())}
        </Typography>
      )}
      {allExternalSystemDisplayNames.length > 1 && (
        <Typography>
          This record is linked to{" "}
          {allExternalSystemDisplayNames.filter((x) => x.length > 0).join(", ")}
        </Typography>
      )}
    </Stack>
  );
};

export const StartIcon = ({
  synchronizing,
  badgeColor,
}: {
  synchronizing: boolean;
  badgeColor?: "error" | "warning";
}) => {
  return (
    <Box sx={{ display: "flex", width: "1.5rem" }}>
      {synchronizing ? (
        <CircularProgress size={"1rem"} data-testid="spinner" />
      ) : badgeColor === undefined ? (
        <CloudDownloadIcon />
      ) : (
        <Badge
          data-testid={`badge-${badgeColor}`}
          color={badgeColor}
          variant="dot"
          anchorOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          <CloudDownloadIcon />
        </Badge>
      )}
    </Box>
  );
};
